import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CreateListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "", city: user?.city || "", province: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Seleziona una categoria");
    setSaving(true);
    try {
      await api.post("/listings", { ...form, images });
      toast.success("Annuncio inviato! Sarà pubblicato dopo l'approvazione.");
      navigate("/profile");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight">Dona un oggetto</h1>
      <p className="text-muted-foreground mt-2">Il tuo annuncio verrà moderato prima di essere pubblicato.</p>

      {!user?.email_verified && (
        <div className="mt-6 rounded-xl bg-accent/10 border border-accent/30 p-4 text-sm" data-testid="verify-warning">
          Verifica la tua email dal <span className="font-semibold">Profilo</span> per poter pubblicare annunci.
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <Label>Foto</Label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border">
                <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/60 rounded-full p-1" data-testid={`remove-img-${i}`}>
                  <X className="h-3 w-3 text-white" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors text-muted-foreground" data-testid="upload-label">
              {uploading ? <UploadCloud className="h-6 w-6 animate-pulse" /> : <ImagePlus className="h-6 w-6" />}
              <span className="text-xs mt-1">Aggiungi</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="upload-input" />
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Titolo</Label>
          <Input id="title" value={form.title} onChange={set("title")} required data-testid="listing-title" placeholder="Es. Divano 2 posti" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="desc">Descrizione</Label>
          <Textarea id="desc" value={form.description} onChange={set("description")} required data-testid="listing-desc" placeholder="Condizioni, dimensioni, motivo..." className="rounded-xl min-h-28" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="listing-category"><SelectValue placeholder="Scegli" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">Città</Label>
            <Input id="city" value={form.city} onChange={set("city")} required data-testid="listing-city" placeholder="Milano" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prov">Provincia</Label>
            <Input id="prov" value={form.province} onChange={set("province")} data-testid="listing-province" placeholder="MI" className="h-11 rounded-xl" />
          </div>
        </div>

        <Button type="submit" disabled={saving || !user?.email_verified} data-testid="listing-submit" className="w-full h-12 rounded-full text-base">
          {saving ? "Invio..." : "Pubblica annuncio"}
        </Button>
      </form>
    </div>
  );
}
